import { parsePhoneNumberFromString } from 'libphonenumber-js';

function extractDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/** Display NA numbers with +1 country code; preserves leading + for other formats. */
export function formatPhoneForTenantPortalDisplay(phone: string | null | undefined): string {
  if (!phone?.trim()) return '—';
  const trimmed = phone.trim();
  const digits = extractDigits(trimmed);

  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    const local = digits.slice(1);
    return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (trimmed.startsWith('+')) {
    return trimmed;
  }

  return trimmed;
}

/**
 * Leave partial numbers as plain digits so backspace works.
 * Format a North American number once it has 10 digits, and an international
 * number once it reaches a valid length for its country.
 */
export function formatPhoneInputLive(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const hasPlus = trimmed.startsWith('+');
  const digits = extractDigits(trimmed);
  if (!digits) return hasPlus ? '+' : '';

  const parsed = hasPlus
    ? parsePhoneNumberFromString(`+${digits}`)
    : parsePhoneNumberFromString(digits, 'CA');

  if (parsed?.countryCallingCode === '1' && parsed.nationalNumber.length === 10) {
    const national = parsed.nationalNumber;
    return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }

  if (parsed?.isValid() && parsed.countryCallingCode !== '1') {
    return parsed.formatInternational();
  }

  return hasPlus ? `+${digits}` : digits;
}

function nanpDigitsForCompare(phone: string): string {
  let digits = extractDigits(phone);
  if (digits.length === 10) digits = `1${digits}`;
  return digits;
}

/** Whether a formatted input matches an E.164 value from the API. */
export function phoneInputMatchesE164(input: string, phoneE164: string): boolean {
  const a = nanpDigitsForCompare(input);
  const b = nanpDigitsForCompare(phoneE164);
  return Boolean(a && b && a === b);
}

/** True when the number is a United States NANP number (not Canada). */
export function isUnitedStatesPhone(phone: string | null | undefined): boolean {
  if (!phone?.trim()) return false;
  const trimmed = phone.trim();
  const digits = extractDigits(trimmed);
  if (digits.length < 10) return false;
  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(digits.length === 10 ? `+1${digits}` : `+${digits}`);
  return parsed?.country === 'US';
}

/** Canadian numbers can be texted a login code. Anything else continues with email. */
export function loginPhoneKind(phone: string | null | undefined): 'canadian' | 'other' | 'incomplete' {
  if (!phone?.trim()) return 'incomplete';
  const trimmed = phone.trim();
  const digits = extractDigits(trimmed);
  if (digits.length < 10) return 'incomplete';
  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(digits.length === 10 ? `+1${digits}` : `+${digits}`);
  if (!parsed?.isValid()) return 'incomplete';
  return parsed.country === 'CA' ? 'canadian' : 'other';
}

/** True when the number is a Canadian NANP number. */
export function isCanadianPhone(phone: string | null | undefined): boolean {
  if (!phone?.trim()) return false;
  const trimmed = phone.trim();
  const digits = extractDigits(trimmed);
  if (digits.length < 10) return false;
  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(digits.length === 10 ? `+1${digits}` : `+${digits}`);
  return parsed?.country === 'CA';
}

/** E.164-ish value for clipboard (NANP → +1XXXXXXXXXX). */
export function phoneCopyValue(phone: string | null | undefined): string | null {
  if (!phone?.trim()) return null;
  const trimmed = phone.trim();
  const digits = extractDigits(trimmed);
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (trimmed.startsWith('+')) return trimmed.replace(/\s/g, '');
  if (digits.length > 0) return `+${digits}`;
  return trimmed;
}
